"use client";

import { useEffect } from "react";
import { gridMetrics } from "@/components/hero/HeroGrid";

/**
 * Row-aligns a band section's grid blocks: every [data-grid-block]'s top is
 * pushed down (margin-top) to the nearest grid row line that leaves at least
 * ONE complete free grid row after the previous clearing edge (the band's
 * top line for the first block), and the last block gets one cell of
 * margin-bottom so a full free row always fits before the band's bottom
 * line. Pass a ref to any element INSIDE the section (the hook walks up via
 * closest("section")). Re-runs on resize and fonts.ready; dispatches
 * grid-relayout when anything changed so RowBand/GridClearing re-snap.
 * Desktop-only (<768px clears its inline styles); call AFTER any hook that
 * sizes block heights so measurements see final heights.
 *
 * ASSUMES stable post-layout block heights (#189): in-block animation must be
 * transform/opacity-only — the kit convention. An animation that changes a
 * block's layout height mid-flight would shift the measured tops and fight
 * the aligner (the ResizeObserver would re-run every frame and margins would
 * churn). Reserve final heights up front (minHeight, stacked grid areas).
 *
 * Also re-runs on grid-relayout (dispatched by other sub-cell layout
 * shifters, e.g. a height-adjusting block above this one in the same band) —
 * safe because the dispatch above is change-guarded, so this can't loop.
 *
 * Iterative settle: a single pass computes each block's margin-top from its
 * CURRENT measured top, but CSS margin collapsing merges an assigned
 * margin-top into the previous sibling's own margin-bottom (e.g.
 * SectionHead's `mb-[54px]`) — the two don't sum, they collapse to the max —
 * so one pass under-corrects later blocks. Running the same measure/assign
 * logic for up to 3 passes (adding each pass's delta to the already-assigned
 * margin rather than overwriting it) lets the layout settle onto the grid
 * once collapsing has been absorbed, and stops early once every block's
 * measured top lands on a row line with a free row before it.
 */
export default function useBandRowAlign(innerRef: React.RefObject<HTMLElement | null>) {
  useEffect(() => {
    let raf = 0;
    // 8, not 3: a margin collapsing into a large sibling margin-bottom (e.g.
    // SectionHead's mb-[54px]) absorbs each pass's constant-size delta until
    // the assigned margin exceeds the sibling's — that can take 4-5 passes.
    const MAX_PASSES = 8;
    const layout = () => {
      raf = 0;
      const section = innerRef.current?.closest("section");
      if (!section) return;
      const blocks = Array.from(section.querySelectorAll<HTMLElement>("[data-grid-block]"));
      if (!blocks.length) return;
      if (document.documentElement.clientWidth < 768) {
        blocks.forEach((b) => {
          b.style.marginTop = "";
          b.style.marginBottom = "";
        });
        return;
      }
      const { cell } = gridMetrics(document.documentElement.clientWidth);
      const prevKey = blocks.map((b) => `${b.style.marginTop}|${b.style.marginBottom}`).join(";");
      // Reset before measuring, but ONLY on the first pass — later passes
      // must build on the margins the previous pass assigned, since margin
      // collapsing means the natural top still reflects the un-collapsed
      // portion of earlier passes' corrections.
      blocks.forEach((b) => {
        b.style.marginTop = "";
        b.style.marginBottom = "";
      });
      let settled = false;
      // EPS: edges engineered to land exactly ON a row line (whole-row blocks,
      // band butts, 1px anti-collapse section paddings) must not make ceil
      // jump a whole row. 2.5 (not 1.5): two flush sections meeting contribute
      // BOTH their 1px anti-collapse paddings (2px total) — the boundary of
      // back-to-back `md:pt-px md:pb-px` sections, the pattern that yields the
      // owner's rhythm of exactly two free rows between sections.
      const EPS = 2.5;
      for (let pass = 0; pass < MAX_PASSES && !settled; pass++) {
        const secTop = section.getBoundingClientRect().top + window.scrollY;
        // The band's top edge snaps to the first row line at/after the section top.
        let prevEdgeRow = Math.ceil((secTop - EPS) / cell);
        settled = true;
        for (const b of blocks) {
          const natTop = b.getBoundingClientRect().top + window.scrollY;
          const minTop = (prevEdgeRow + 1) * cell;
          const alignedTop = Math.max(minTop, Math.ceil((natTop - EPS) / cell) * cell);
          if (Math.abs(alignedTop - natTop) > 1) settled = false;
          const current = parseFloat(b.style.marginTop) || 0;
          const next = current + (alignedTop - natTop);
          b.style.marginTop = next > 0.5 ? `${next}px` : "";
          const h = b.getBoundingClientRect().height;
          prevEdgeRow = Math.ceil((alignedTop + h - EPS) / cell);
        }
      }
      // One guaranteed free row between the last clearing edge and the
      // band's bottom line (SectionShell's own padding alone can leave 0
      // once the clearing snaps outward).
      blocks[blocks.length - 1].style.marginBottom = `${cell}px`;
      const nextKey = blocks.map((b) => `${b.style.marginTop}|${b.style.marginBottom}`).join(";");
      if (nextKey !== prevKey) window.dispatchEvent(new Event("grid-relayout"));
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(layout);
    };
    layout();
    document.fonts.ready.then(schedule).catch(() => {});
    window.addEventListener("resize", schedule);
    window.addEventListener("grid-relayout", schedule);
    // Content heights can change without a resize or relayout event (late
    // font swaps re-flowing a block) — margins computed from stale tops then
    // leave blocks off their row lines. Settles: a re-run that changes
    // nothing writes identical margins and fires no further size change.
    const section = innerRef.current?.closest("section");
    const ro = section ? new ResizeObserver(schedule) : null;
    if (section && ro) ro.observe(section);
    return () => {
      window.removeEventListener("resize", schedule);
      window.removeEventListener("grid-relayout", schedule);
      ro?.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [innerRef]);
}
